/**
 * @Author: Jon Lucas
 * @Date:   2017-09-12T09:23:57+02:00
 * @Email:  jon.lucas@mazedia.fr
 * @Last modified by:   Jon Lucas
 * @Last modified time: 2017-09-29T11:45:32+02:00
 */

import { Component, ViewChild, Input } from "@angular/core";

import { AbstractViewComponent } from "../abstract-view/abstract-view.component";

import { BaseDataType } from "../../base-data-type";

import { ConfDataType } from "../../conf-data-type";
import { ActivatedRoute, Router, NavigationEnd } from "@angular/router";
import { CamConfType } from "./threed-view/cam-conf-type";
import { AppService } from "../../app.service";
import { ThreedViewComponent } from "./threed-view/threed-view.component";
import { ThreedLandmarkComponent } from "./threed-view/threed-landmark.component";
import * as THREE from "three";
import { Utils } from "../../util/Utils";
import { AppLoadingStateService } from "../../app-loading-state.service";
import { CameraOptions } from "../../full-screen/full-screen.component";
import { environment } from "../../../environments/environment";

@Component({
    selector: "app-building-customization",
    templateUrl: "./building-customization.component.html",
    styleUrls: ["./building-customization.component.scss"]
})

export class BuildingCustomizationComponent extends AbstractViewComponent {

    public readonly TAG: string = "[BuildingCustomizationComponent]";

    @Input() params: CameraOptions;
    @ViewChild(ThreedViewComponent)
    private threedViewComponent: ThreedViewComponent;

    // Store requested object's values here
    public configDictionary: { [key: string]: string | string[] } = {};

    public cameraDefaultsConf: CamConfType;
    public landmarks: BaseDataType[];

    private landmarkComponents: ThreedLandmarkComponent[];

    private lmSprites: THREE.Sprite[];

    private navSubsectionTypeById: { [key: string]: BaseDataType };
    private sectionIdBySubsectionId: { [key: string]: string };

    private _mouseClickEventHandlerBinded: (e: MouseEvent | CustomEvent) => void;
    private _mouseDownEventHandlerBinded: (e: MouseEvent | CustomEvent) => void;
    private _mouseUpEventHandlerBinded: (e: MouseEvent | CustomEvent) => void;

    private _raycaster: THREE.Raycaster;
    private _mouse: THREE.Vector2;

    constructor(protected route: ActivatedRoute) {
        super(route);

        this._raycaster = new THREE.Raycaster();
        this._mouse = new THREE.Vector2();

        this.route.url.subscribe( url =>
        {
            if (url.length > 2 && url[3].path === "belriv-type")
            {
                this.threedViewComponent.setBelrivView();
            }
        });
    }

    /**
     * Get user configuration for given json id.
     *
     * Stores main value in 0, and optional value in 1.
     *
     * Example with color value only :
     * ["#00FF00"]
     *
     * Example with a texture with tint value :
     * ["assets/texture.jpg", "#00FF00"]
     *
     * If an obj is present in the values array, stores it instead of the image (used as thumbnail in the main menu) :
     * ["assets/model.obj", "#00FF00"]
     *
     * @param pId
     */
    private getType(pId: string): void {
        console.log(this.TAG, "getType()", pId);

        const confData: ConfDataType = this.confDataService.confdata[pId];

        if (!confData || !confData.values || confData.values.length === 0) {
            console.log(this.TAG, pId, this.configDictionary[pId]);
            if (pId === "project-type") { return; }
            this.configDictionary[pId] = [];
            return;
        }

        if (confData.apiurl === undefined || typeof(confData.values[0]) !== "string")   { return; }

        this.appService.getType(confData.apiurl, confData.values[0])
            .then((type: BaseDataType) => {

                if (!type || !type.values || type.values.length === 0) {
                    return;
                }

                console.log(this.TAG, pId, type);
                if (pId === "project-type") {
                    this.configDictionary[pId] = type.id;
                }
                else {
                    let values: string[] = type.values.slice(0);
                    // If multiple values like a thumbnail image and an obj model,
                    // store the obj model only.
                    if (values.length > 1) {
                        if (values[1].indexOf("#") === -1)
                        {
                            values = [values.find(v => v.indexOf("obj") !== -1) || values[1]];
                        }
                        else
                        {
                            // TODO: If thumbnail and color selection ?
                            values = [values[0]];
                        }
                    }
                    if (confData.options && confData.options.length > 0 && type.options && type.options.length > 0 && type.options[0].values) {
                        const option: any = type.options[0].values.find(opt => opt.id === confData.options[0]);
                        if (option.values && option.values.length > 0) {
                            values.push(option.values[0]);
                        }
                    }
                    this.configDictionary[pId] = values;
                }

                console.log(this.TAG, "getType() confdata updated", pId, this.configDictionary[pId]);
            })
            .catch(this.handleError.bind(this));
    }

    private handleError(error: any): void {
        console.error(this.TAG, "An error occurred", error);
    }

    public getData(): void {
        super.getData();
        this.appService.getType(AppService.CAMERA_DEFAULTS_URL, "camera-defaults")
        .then(camconf => {
                this.cameraDefaultsConf = camconf.values[0];
                if (this.params !== undefined ) {
                    const firstParams = this.params[0];
                    if ( firstParams.zoom && firstParams.pan && firstParams.tilt) {
                        this.cameraDefaultsConf.pan =  firstParams.pan;
                        this.cameraDefaultsConf.fov = firstParams.zoom;
                        this.cameraDefaultsConf.tilt = firstParams.tilt;
                    }
                }
        });
        this.appService.getData(AppService.NAVIGATION_MENU_URL)
            .then(types => {
                this.navSubsectionTypeById = {};
                this.sectionIdBySubsectionId = {};
                for (const type of types) {
                    for (const value of type.values) {
                        this.navSubsectionTypeById[value.id] = value;
                        this.sectionIdBySubsectionId[value.id] = type.id;
                    }
                }
            });

        this.appService.getData(AppService.THREED_VIEW_LANDMARKS_URL)
            .then(types => {
                this.landmarks = types;
            });
    }

    protected updateLandmarks(): void {
        console.log(this.TAG, "updateLandmarks()");

        if (this.threedViewComponent.scene === undefined)    { return; }

        const maxDim: number = this.threedViewComponent.getMaxDimension();

        if (this.landmarkComponents === undefined) {
            this.landmarkComponents = [];
            this.lmSprites = [];
            let lmComp: ThreedLandmarkComponent;
            let bgColor: string;
            for (const type of this.landmarks) {
                console.log(this.TAG, "createLandmark()", type.id);
                bgColor = environment.landmarks_bg_color || "#e2202b";
                lmComp = new ThreedLandmarkComponent(type, this.lang, this.threedViewComponent.scene, this.threedViewComponent.getMaxDimension() / 500, 256, bgColor);
                lmComp.sprite.position.set(maxDim * type.values[0], maxDim * type.values[1], maxDim * type.values[2]);
                this.landmarkComponents.push(lmComp);
                this.lmSprites.push(lmComp.sprite);
            }
        }

        for (const lm of this.landmarkComponents) {
            lm.sprite.visible = Utils.conditionsValidated(this.navSubsectionTypeById[lm.type.id]);
        }
    }

    protected _mouseClickEventHandler(e: MouseEvent | CustomEvent): void {
        e.preventDefault();

        console.log(this.TAG, "_mouseClickEventHandler()", e);

        const pageX: number = e instanceof CustomEvent ? (<CustomEvent>e).detail.pageX : (<MouseEvent>e).layerX;
        const pageY: number = e instanceof CustomEvent ? (<CustomEvent>e).detail.pageY : (<MouseEvent>e).layerY;

        this._mouse.x = (pageX / this.threedViewComponent.getViewWidth()) * 2 - 1;
        this._mouse.y = - (pageY / this.threedViewComponent.getViewHeight()) * 2 + 1;

        this._raycaster.setFromCamera(this._mouse, this.threedViewComponent.camera);
        const intersects: THREE.Intersection[] = this._raycaster.intersectObjects(this.lmSprites);

        if (intersects.length > 0) {
            console.log(intersects[0].object.name);
            this.landmarkSelected(intersects[0].object.name);
        }
    }

    protected _mouseDownEventHandler(e: MouseEvent | CustomEvent):void {
        event.preventDefault();

        console.log(this.TAG, "_mouseDownEventHandler()", e);
        this._mouseClickEventHandler(e);

    }

    protected _mouseUpEventHandler(e: MouseEvent | CustomEvent):void {

        console.log(this.TAG, "_mouseUpEventHandler()", e);

    }

    private landmarkSelected(pId: string): void {
        // alert("Landmark selected: " + pId);

        this.router.navigate(["/lang", this.lang, this.sectionIdBySubsectionId[pId], pId]);
    }

    /**
     * 3D Base model loaded event handler.
     *
     * @param pId {string}
     */
    public onModelLoaded(pId: string): void {
        console.log(this.TAG, "onModelLoaded", pId);

        this.forceThreedModelValuesUpdate();

        if (pId === "base-model") {
            this.updateLandmarks();
        }

        this.addListeners();
    }

    /**
     * Force threedmodel binded values update like textures and colors.
     */
    public forceThreedModelValuesUpdate(): void {
        for (const key in this.confDataService.confdata) {
            if (!this.confDataService.confdata[key]) { continue; }
            this.getType(key);
        }
    }

    protected initBindedEvents(): void {
        super.initBindedEvents();

        this._mouseClickEventHandlerBinded = this._mouseClickEventHandler.bind(this);
        this._mouseDownEventHandlerBinded = this._mouseDownEventHandler.bind(this);
        this._mouseUpEventHandlerBinded = this._mouseUpEventHandler.bind(this);

    }

    public addListeners(): void {
        super.addListeners();

        this.threedViewComponent.renderer.domElement.addEventListener("click", this._mouseClickEventHandlerBinded, false);
        this.threedViewComponent.renderer.domElement.addEventListener("mousedown", this._mouseDownEventHandlerBinded, false);
        this.threedViewComponent.renderer.domElement.addEventListener("mouseup", this._mouseUpEventHandlerBinded, false);
        // Mz.Util.DomInteractionUtils.detectClicks(this.threedViewComponent.renderer.domElement, 5, true);
        // this.threedViewComponent.renderer.domElement.addEventListener( Mz.Util.DomInteractionEvent.CLICK_DETECTED, this._mouseClickEventHandlerBinded, false );
    }

    public removeListeners(): void {
        super.removeListeners();

        this.threedViewComponent.renderer.domElement.removeEventListener("click", this._mouseClickEventHandlerBinded, false);
        this.threedViewComponent.renderer.domElement.removeEventListener("mousedown", this._mouseDownEventHandlerBinded, false);
        this.threedViewComponent.renderer.domElement.removeEventListener("mouseup", this._mouseUpEventHandlerBinded, false);
        // Mz.Util.DomInteractionUtils.undetectClicks(this.threedViewComponent.renderer.domElement, 5, true);
        // this.threedViewComponent.renderer.domElement.removeEventListener( Mz.Util.DomInteractionEvent.CLICK_DETECTED, this._mouseClickEventHandlerBinded, false );
    }

    public onLangChanged(): void {
        super.onLangChanged();

        if (!this.landmarkComponents) { return; }

        for (const lmComp of this.landmarkComponents) {
            lmComp.updateLang(this.lang);
        }
    }

    public ngOnInit(): void {
        super.ngOnInit();

        this.confDataService.observable.subscribe(
            (value) => {
                this.getType(value.id);
                this.updateLandmarks();
            }
        );
    }
}



// WEBPACK FOOTER //
// ./src/app/main-view/building-customization/building-customization.component.ts